/**
 * GlobalState - Centralized game state
 *
 * Tracks all game state that persists across ticks:
 * - Stars collected
 * - Keys in inventory
 * - Player powerups (flippers, suction boots)
 * - Button press counts
 * - Deaths, timer, etc.
 *
 * Accessed by entities via MoveContext when making decisions.
 * Updated by AutomataCoordinator when applying MoveResults.
 */

export class GlobalState {
  // Star collection
  starsCollected: number = 0;
  totalStars: number = 0;

  // Key inventory
  keys: Set<string> = new Set(); // 'green', 'red', 'orange', 'blue'

  // Player powerups
  hasFlippers: boolean = false;
  hasSuctionBoots: boolean = false;

  // Button system
  buttonPressCount: number = 0;
  pressedButtons: Map<string, Set<string>> = new Map(); // buttonKey -> Set<entityId>

  // Game stats
  deaths: number = 0;
  timer: number = 0;

  constructor(totalStars: number) {
    this.totalStars = totalStars;
  }

  /**
   * Check if player has a specific key
   */
  hasKey(color: string): boolean {
    return this.keys.has(color);
  }

  /**
   * Add a key to inventory
   */
  addKey(color: string): void {
    this.keys.add(color);
  }

  /**
   * Remove a key from inventory (when used to open a door)
   */
  removeKey(color: string): void {
    this.keys.delete(color);
  }

  /**
   * Check if all stars collected
   */
  hasAllStars(): boolean {
    return this.starsCollected >= this.totalStars;
  }

  /**
   * Increment star count
   */
  collectStar(): void {
    this.starsCollected++;
  }

  /**
   * Press a button (entity id presses button at position)
   */
  pressButton(): void {
    this.buttonPressCount++;
  }

  /**
   * Release a button (entity id stops pressing button at position)
   */
  releaseButton(): void {
    this.buttonPressCount -= 1;
  }

  /**
   * Check if a button is currently pressed
   */
  isButtonPressed(buttonKey: string): boolean {
    const pressers = this.pressedButtons.get(buttonKey);
    return pressers ? pressers.size > 0 : false;
  }

  numButtonsPressed(): number {
    return this.buttonPressCount;
  }

  /**
   * Get all collected items in priority order for display
   * Returns array of item info with texture paths
   */
  getCollectedItems(): Array<{ texture: string, label: string }> {
    const items: Array<{ texture: string, label: string }> = [];

    // Keys in order: green, red, orange, blue
    if (this.hasKey('green')) {
      items.push({ texture: 'tiles/green-key', label: 'green-key' });
    }
    if (this.hasKey('red')) {
      items.push({ texture: 'tiles/red-key', label: 'red-key' });
    }
    if (this.hasKey('orange')) {
      // Orange key uses yellow-key texture (matches KeyTile implementation)
      items.push({ texture: 'tiles/yellow-key', label: 'orange-key' });
    }
    if (this.hasKey('blue')) {
      items.push({ texture: 'tiles/blue-key', label: 'blue-key' });
    }

    // Flippers
    if (this.hasFlippers) {
      items.push({ texture: 'tiles/flippers', label: 'flippers' });
    }

    // Suction boots
    if (this.hasSuctionBoots) {
      items.push({ texture: 'tiles/suction-boots', label: 'suction-boots' });
    }

    return items;
  }

  /**
   * Reset state for new level
   */
  reset(totalStars: number): void {
    this.starsCollected = 0;
    this.totalStars = totalStars;
    this.keys.clear();
    this.hasFlippers = false;
    this.hasSuctionBoots = false;
    this.buttonPressCount = 0;
    this.pressedButtons.clear();
    // deaths and timer persist across levels
  }
}
